export type ShareContextLevel = 'page' | 'pageAndChildren' | 'pageChildrenAndRelated';

/** The level every share-context entry sends: the menu has no picker for the others (CCINT-9384). */
export const DEFAULT_SHARE_CONTEXT_LEVEL: ShareContextLevel = 'pageAndChildren';

const TWG_CLI_INSTALL_URL =
	'https://developer.atlassian.com/platform/teamwork-graph/twg-cli/getting-started/installation/';
const ROVO_MCP_INSTALL_URL =
	'https://developer.atlassian.com/cloud/rovo-mcp/guides/getting-started/';

const CHILDREN_TASK_LINE =
	'Then fetch the child page tree. Retrieve titles only. Fetch full content for a child page only if its title is relevant to the task.';

const RELATED_TASK_LINE = 'Then fetch all incoming, outgoing, and related content for the page.';

/**
 * The task lines each level adds between fetching the page and using what was retrieved. Keyed by
 * level rather than composed from booleans so a new level cannot silently inherit another's steps,
 * and so `Record` forces this map to be filled in when one is added.
 */
const TASK_LINES_BY_LEVEL: Record<ShareContextLevel, readonly string[]> = {
	page: [],
	pageAndChildren: [CHILDREN_TASK_LINE],
	pageChildrenAndRelated: [CHILDREN_TASK_LINE, RELATED_TASK_LINE],
};

export type ShareContextPromptOptions = {
	contentId?: string;
	cloudId?: string;
	contextLevel: ShareContextLevel;
};

/**
 * The prompt the surface links carry and the prompt block shows. The content title is left out
 * on purpose: the byline has no reliable source for it, and the CLI resolves the page from its id.
 *
 * Plain text, not translated — it is instructions for an AI surface, not user-facing copy.
 */
export const buildShareContextPrompt = ({
	contentId,
	cloudId,
	contextLevel,
}: ShareContextPromptOptions): string => {
	const taskLines = [
		`Fetch the content for ${contentId ?? '<CONTENT_ID>'}.`,
		...TASK_LINES_BY_LEVEL[contextLevel],
		'Use all retrieved content as context for subsequent tasks.',
	];

	return [
		'## Context',
		`Content ID: ${contentId ?? '<CONTENT_ID>'}`,
		`Cloud ID: ${cloudId ?? '<CLOUD_ID>'}`,
		'',
		'## Setup',
		'Use the Teamwork Graph CLI if available. If not, check if the Atlassian Rovo MCP Server is connected.',
		'',
		'If neither is available, ask the user which they would like to install:',
		`- TWG CLI: ${TWG_CLI_INSTALL_URL}`,
		`- Atlassian Rovo MCP Server: ${ROVO_MCP_INSTALL_URL}`,
		'',
		'If the chosen installation fails, offer the alternative. Guide the user through authentication when required.',
		'',
		'## Task',
		...taskLines,
	].join('\n');
};
